import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Paintbrush, X, RotateCw, Move, Trash2 } from 'lucide-react';

const FABRIC_COLORS = [
  { name: 'Ivory', value: '#f5f0e8' },
  { name: 'Tan', value: '#d4a574' },
  { name: 'Black', value: '#1a1a1a' },
  { name: 'Brown', value: '#5c3d2e' },
  { name: 'Maroon', value: '#722f37' },
  { name: 'Navy', value: '#1e3a5f' },
  { name: 'Grey', value: '#6b7280' },
  { name: 'White', value: '#f8f8f8' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Blue', value: '#2563eb' },
];

const WOOD_FINISHES = [
  { name: 'Walnut', value: '#5c3d2e' },
  { name: 'Oak', value: '#d4a574' },
  { name: 'Mahogany', value: '#4a1a0a' },
  { name: 'White', value: '#f0ece4' },
  { name: 'Black', value: '#1a1a1a' },
  { name: 'Teak', value: '#b8860b' },
];

const CARPET_COLORS = [
  { name: 'Charcoal', value: '#333333' },
  { name: 'Beige', value: '#d4c5a9' },
  { name: 'Navy', value: '#1a1a4e' },
  { name: 'Maroon', value: '#5a0a0a' },
  { name: 'Grey', value: '#808080' },
  { name: 'Black', value: '#0a0a0a' },
];

export default function MaterialEditor({ 
  selectedInstance, comp, customMaterials, onMaterialChange, onClose,
  onMove, onRotate, onRemove, placedComponent
}) {
  if (!selectedInstance || !comp) return null;

  const current = customMaterials[selectedInstance] || {};
  const isSeating = comp.category === 'Seating';
  const isWood = comp.category === 'Storage' || comp.category === 'Utility';
  const rotation = placedComponent?.rotation || 0;

  const handleChange = (key, value) => {
    onMaterialChange(selectedInstance, { ...current, [key]: value });
  };

  return (
    <div className="glass-panel rounded-xl p-4 mb-3 border border-white/5" data-testid="material-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Paintbrush className="w-4 h-4 text-blue-400" />
          <span className="text-xs font-semibold text-white">Customize: {comp.name}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0 text-slate-400" data-testid="material-close-btn">
          <X className="w-3 h-3" />
        </Button>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 mb-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onMove && onMove(selectedInstance)}
          className="flex-1 h-8 text-xs border-slate-700 text-slate-300 hover:bg-slate-800"
          data-testid="move-selected-btn"
        >
          <Move className="w-3 h-3 mr-1" />
          Move
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onRotate && onRotate(selectedInstance)}
          className="flex-1 h-8 text-xs border-slate-700 text-slate-300 hover:bg-slate-800"
          data-testid="rotate-selected-btn"
        >
          <RotateCw className="w-3 h-3 mr-1" />
          Rotate ({rotation}°)
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onRemove && onRemove(selectedInstance)}
          className="h-8 px-2 text-xs border-red-900/50 text-red-400 hover:bg-red-900/20"
          data-testid="remove-selected-btn"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      <ScrollArea className="max-h-48">
        <div className="space-y-3">
          {/* Fabric / Leather Color (for seating) */}
          {isSeating && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2 block">Leather / Fabric Color</label>
              <div className="flex flex-wrap gap-1.5">
                {FABRIC_COLORS.map(c => (
                  <button
                    key={c.value}
                    data-testid={`fabric-${c.name.toLowerCase()}`}
                    onClick={() => handleChange('fabricColor', c.value)}
                    className={`w-7 h-7 rounded-md border-2 transition-all ${
                      current.fabricColor === c.value ? 'border-blue-400 scale-110' : 'border-transparent hover:border-white/20'
                    }`}
                    style={{ background: c.value }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Wood Finish (for storage/utility) */}
          {isWood && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2 block">Wood Finish</label>
              <div className="flex flex-wrap gap-1.5">
                {WOOD_FINISHES.map(c => (
                  <button
                    key={c.value}
                    data-testid={`wood-${c.name.toLowerCase()}`}
                    onClick={() => handleChange('woodFinish', c.value)}
                    className={`w-7 h-7 rounded-md border-2 transition-all ${
                      current.woodFinish === c.value ? 'border-blue-400 scale-110' : 'border-transparent hover:border-white/20'
                    }`}
                    style={{ background: c.value }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Accent Color (for all) */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2 block">Accent Color</label>
            <div className="flex flex-wrap gap-1.5">
              {CARPET_COLORS.map(c => (
                <button
                  key={c.value}
                  data-testid={`accent-${c.name.toLowerCase()}`}
                  onClick={() => handleChange('color', c.value)}
                  className={`w-7 h-7 rounded-md border-2 transition-all ${
                    current.color === c.value ? 'border-blue-400 scale-110' : 'border-transparent hover:border-white/20'
                  }`}
                  style={{ background: c.value }}
                  title={c.name}
                />
              ))}
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
